import { z } from 'zod';
import { ARRANGEMENT_TYPE_KEYS } from '../constants/arrangementType.constant';
import { ARRANGEMENT_STATUSES, calendarDateSchema } from './arrangement.schema';
import { templateProducedSchema } from './arrangementTemplate.schema';
import { objectIdSchema } from './common.schema';
import { TEMPLATE_DECISION_CLASSES, templateSetKeySchema } from './templateSet.schema';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B §4] FROM TEMPLATES TO ORDERS — THE CALENDAR, THE PLAN, AND THE RUN.
 *
 *  ⚠️ NOTHING IS WRITTEN BEFORE APPROVAL. The plan is a pure read; it returns what will be created and
 *  a digest. Approving sends the digest back, and the server re-plans and refuses (409) if anything
 *  moved since the screen was read.
 *
 *  ⚠️ A RUN IS ADVANCED IN BATCHES, NEVER ONE TRANSACTION ACROSS THE SET. Each order is created or
 *  published by the existing writer in its own transaction; the run remembers which items are done,
 *  and its report is read back from the orders themselves, so a half-run says what was written.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export const TEMPLATE_YEAR_MIN = 2000;
export const TEMPLATE_YEAR_MAX = 2100;
export const templateYearSchema = z.coerce.number().int().min(TEMPLATE_YEAR_MIN).max(TEMPLATE_YEAR_MAX);

/** How many items one run may hold. A set's templates, split into parts, stay well inside it. */
export const TEMPLATE_RUN_MAX_ITEMS = 1000;

// ── The calendar ──────────────────────────────────────────────────────────────────

/**
 * ⚠️ [P7B §4b] EACH FESTIVAL DAY TAKES ITS OWN DATE. An anchor fills the rest as a convenience, and
 * every day can then be moved on its own; two days may share a date. `null` is a day not yet dated.
 */
export const templateCalendarSchema = z.object({
  setKey: z.string(),
  year: z.number().int(),
  /** The arrangement type orders for this year will carry, or `null` when none is registered. */
  type: z.enum(ARRANGEMENT_TYPE_KEYS).nullable(),
  days: z.array(z.object({ dayKey: z.string(), date: calendarDateSchema.nullable() })),
  updatedAt: z.iso.datetime().nullable(),
  updatedBy: z.object({ id: objectIdSchema, name: z.string() }).nullable(),
});

export const templateCalendarParamsSchema = z.object({ setKey: templateSetKeySchema, year: templateYearSchema });

export const putTemplateCalendarSchema = z.object({
  days: z
    .array(z.object({ dayKey: z.string().min(1), date: calendarDateSchema }))
    .min(1)
    .refine((days) => new Set(days.map((day) => day.dayKey)).size === days.length, { message: 'A day is dated twice' }),
});

// ── The plan ──────────────────────────────────────────────────────────────────────

export const templatePlanInputSchema = z.object({
  templateIds: z
    .array(objectIdSchema)
    .min(1)
    .max(TEMPLATE_RUN_MAX_ITEMS)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'A template is chosen twice' }),
  year: z.number().int().min(TEMPLATE_YEAR_MIN).max(TEMPLATE_YEAR_MAX),
});

export const templatePlanItemSchema = z.object({
  /** `t12#2` — a template and its part. */
  key: z.string(),
  templateId: objectIdSchema,
  templateKey: z.string(),
  part: z.number().int().min(1),
  parts: z.number().int().min(1),
  title: z.string(),
  /** One per item, so a run can refuse just the item that moved. */
  digest: z.string(),
  dutyPointCount: z.number().int().min(0),
  deploymentCount: z.number().int().min(0),
  slotCount: z.number().int().min(0),
  /** Every date the draft's duties run on, IST business dates, sorted. */
  dates: z.array(calendarDateSchema),
  /** "Whole day · report at 3:30 PM" or "10:00 PM – 8:00 AM", already worded. */
  times: z.string(),
  /** People per column per source, after every decision. */
  people: z.array(z.object({ column: z.string(), source: z.string(), count: z.number().int().min(0) })),
  /** The in-charge roles created EMPTY on its duty points, by label — never a count. */
  inChargeRoles: z.array(z.string()),
});

export const templatePlanSchema = z.object({
  setKey: z.string(),
  year: z.number().int(),
  type: z.enum(ARRANGEMENT_TYPE_KEYS),
  /** The order date every draft will carry — the IST day the plan was computed. */
  paDate: calendarDateSchema,
  digest: z.string(),
  items: z.array(templatePlanItemSchema),
  blocked: z.array(
    z.object({ templateId: objectIdSchema, templateKey: z.string(), title: z.string(), reasons: z.array(z.string()) }),
  ),
  roundTheClock: z.array(
    z.object({
      templateId: objectIdSchema,
      templateKey: z.string(),
      title: z.string(),
      /**
       * Duty points left out because they are round the clock; empty when the whole template is.
       * [TEMPLATE-FIND] Each in a person's words — `(e)`, or `table 1, row 4` — never a raw key.
       */
      excludedDutyPoints: z.array(z.string()),
      wholeTemplate: z.boolean(),
    }),
  ),
  /** Orders a selected template already produced — re-creating needs an acknowledgement. */
  produced: z.array(
    z.object({ templateId: objectIdSchema, templateKey: z.string(), orders: z.array(templateProducedSchema) }),
  ),
  /** People left out because their source was skipped or their column dropped — stated, not hidden. */
  dropped: z.array(
    z.object({
      templateKey: z.string(),
      /** The duty point's key — for a React key or a link, never shown. */
      dutyPoint: z.string(),
      /** [TEMPLATE-FIND] The duty point as a person reads it: `(b)`, or `table 1, row 34`. */
      dutyPointLabel: z.string(),
      column: z.string(),
      source: z.string().nullable(),
      n: z.number().int().min(0),
      why: z.enum(['SOURCE_SKIPPED', 'COLUMN_SKIPPED', 'DUTY_POINT_SKIPPED', 'UNPARSED']),
    }),
  ),
  /** How every code the plan used was resolved. */
  resolutions: z.array(
    z.object({
      class: z.enum(TEMPLATE_DECISION_CLASSES),
      code: z.string(),
      label: z.string(),
      byRule: z.boolean(),
      decidedBy: z.string().nullable(),
    }),
  ),
  totals: z.object({
    orders: z.number().int().min(0),
    dutyPoints: z.number().int().min(0),
    deployments: z.number().int().min(0),
    slots: z.number().int().min(0),
  }),
});

// ── The run ───────────────────────────────────────────────────────────────────────

export const TEMPLATE_RUN_KINDS = ['CREATE', 'PUBLISH'] as const;
export type TemplateRunKind = (typeof TEMPLATE_RUN_KINDS)[number];

/**
 * What happened to one item, read back from the order wherever an order exists — never inferred from
 * an error class ([P7B §4f]): publish previews before its transaction, so a resumed item past its first
 * duty date fails with a blocker before it could say "already published".
 */
export const TEMPLATE_RUN_OUTCOMES = ['PENDING', 'CREATED', 'PUBLISHED', 'ALREADY_DONE', 'FAILED', 'SKIPPED'] as const;
export type TemplateRunOutcome = (typeof TEMPLATE_RUN_OUTCOMES)[number];

export const createTemplateRunSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('CREATE'),
    templateIds: templatePlanInputSchema.shape.templateIds,
    year: templatePlanInputSchema.shape.year,
    /** The plan's digest. A different digest on re-plan is a 409 — something moved since the screen was read. */
    digest: z.string().min(1),
    /** Templates that already produced an order, created again on purpose. */
    acknowledgeProduced: z.array(objectIdSchema).max(TEMPLATE_RUN_MAX_ITEMS).default([]),
  }),
  z.object({
    kind: z.literal('PUBLISH'),
    arrangementIds: z
      .array(objectIdSchema)
      .min(1)
      .max(TEMPLATE_RUN_MAX_ITEMS)
      .refine((ids) => new Set(ids).size === ids.length, { message: 'An order is listed twice' }),
  }),
]);

export const templateRunItemSchema = z.object({
  key: z.string(),
  templateKey: z.string().nullable(),
  part: z.number().int().nullable(),
  parts: z.number().int().nullable(),
  title: z.string(),
  arrangementId: objectIdSchema.nullable(),
  reference: z.string().nullable(),
  /** The order's status now, read back — `null` when no order exists for the item. */
  status: z.enum(ARRANGEMENT_STATUSES).nullable(),
  outcome: z.enum(TEMPLATE_RUN_OUTCOMES),
  reason: z.string().nullable(),
  at: z.iso.datetime().nullable(),
});

export const templateRunSchema = z.object({
  id: objectIdSchema,
  setKey: z.string(),
  kind: z.enum(TEMPLATE_RUN_KINDS),
  year: z.number().int().nullable(),
  type: z.enum(ARRANGEMENT_TYPE_KEYS),
  /** `DONE` once no item is pending. */
  state: z.enum(['RUNNING', 'DONE']),
  counts: z.object({
    total: z.number().int().min(0),
    pending: z.number().int().min(0),
    done: z.number().int().min(0),
    failed: z.number().int().min(0),
    skipped: z.number().int().min(0),
  }),
  items: z.array(templateRunItemSchema),
  /** Another window is advancing this run right now. */
  leaseHeld: z.boolean(),
  approvedBy: z.object({ id: objectIdSchema, name: z.string() }),
  approvedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
});

export const templateRunParamsSchema = z.object({ runId: objectIdSchema });
export const templateRunCreateParamsSchema = z.object({ setKey: templateSetKeySchema });
